import {
  Dialog,
  DialogBackdrop,
  DialogDescription,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ExclamationIcon } from "@heroicons/react/outline";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";

export const ModalConfirm = ({
  open,
  type,
  setOpen,
  title,
  description,
  detail,
  onConfirm,
  confirmTitle = "OK",
  "data-testid": dataTestId = "",
}) => {
  const { t } = useTranslation();

  return (
    <Transition show={open}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-auto"
        data-testid={dataTestId}
        onClose={setOpen}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <TransitionChild
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop
              transition
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            />
          </TransitionChild>
          {/* Headless UI marks the backdrop aria-hidden, so the panel cannot
              live inside it. This box is the one the backdrop used to give it. */}
          <div className="fixed inset-0">
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                {/* This element is to trick the browser into centering the modal contents. */}
                <div>
                  {type === "SUCCESS" ? (
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                      <CheckIcon
                        className="h-6 w-6 text-green-600"
                        aria-hidden="true"
                      />
                    </div>
                  ) : null}
                  {type === "SMBZERO" ? (
                    <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100">
                      <label
                        className=" text-green-500 text-xl font-bold"
                        aria-hidden="true"
                      >
                        0%
                      </label>
                    </div>
                  ) : null}
                  {type === "WARNING" ? (
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                      <ExclamationIcon
                        className="h-6 w-6 text-yellow-600"
                        aria-hidden="true"
                      />
                    </div>
                  ) : null}
                  {type === "ALERT" ? (
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <XIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                  ) : null}
                  <div className="mt-3 text-center sm:mt-5">
                    <DialogTitle
                      as="h3"
                      className="text-lg leading-6 font-medium text-gray-900"
                    >
                      {t(title)}
                    </DialogTitle>
                    <div className="mt-2">
                      <DialogDescription
                        as="p"
                        className="text-sm text-gray-500"
                      >
                        {t(description)}
                      </DialogDescription>
                      {/* Raw error text, left untranslated so a screenshot
                          shows exactly what failed. */}
                      {detail && (
                        <p className="mt-3 text-xs text-gray-500 font-mono break-words bg-gray-100 rounded-md px-3 py-2">
                          {detail}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => {
                      setOpen(false);
                      if (typeof onConfirm === "function") {
                        onConfirm();
                      }
                    }}
                    title={t(confirmTitle)}
                  />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
